/**
 * Wireframe previews for ONLY the templates the outline endpoint listed —
 * read straight out of each template's own `.pptx`, a few KB at a time,
 * without downloading the template library or the files themselves.
 *
 * WHY NOT THE LIBRARY
 * The library's manifests carry embeddings/vectors and its `.cache` files one
 * base64 image per entry — many MB, all to decorate the handful of ids the
 * outline already named. And a template `.pptx` is no better (mean ~13 MB:
 * each carries its masters, themes and media).
 *
 * HOW: HTTP RANGE READS INTO THE ZIP
 * A `.pptx` is a zip, and a zip is indexed from its END: the end-of-central-
 * directory record says where the central directory is, and the directory
 * says where each part's bytes are. So per file this reads the tail, the
 * directory, then only the small XML parts one slide's geometry needs —
 * presentation (slide order + size), the slide, its layout and its master
 * (placeholders usually inherit their position from those). Azure Blob
 * honours `Range` with a SAS; the directory SAS the dialog already holds
 * covers every file in the folder.
 *
 * Best-effort throughout: a file that can't be read this way (no Range
 * support, ZIP64, a CORS rule without `Range`) just yields no preview — the
 * row keeps its name-only look. Nothing here can fail the step.
 *
 * Runs in the dialog window: a SAS query string is self-contained auth.
 */

import { inflateSync, strFromU8 } from "fflate";
import { directorySasFileUrl, sasVersionHeaders } from "../../shared/services/blobImageUpload";
import { clampPct, roundPct, type SlidePreview, type SlidePreviewBlock, type SlidePreviewRole } from "../../shared/services/slidePreviewGeometry";
import { logSlideTemplatesError } from "../../slide-templates/services/slideTemplatesApi";

const P_NS = "http://schemas.openxmlformats.org/presentationml/2006/main";
const A_NS = "http://schemas.openxmlformats.org/drawingml/2006/main";
const R_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const RELS_NS = "http://schemas.openxmlformats.org/package/2006/relationships";

const EOCD_SIGNATURE = 0x06054b50;
const CD_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;
const EOCD_MIN_SIZE = 22;
/** A `.pptx` carries no zip comment, so the record sits in the last 22 bytes;
 *  a small first read covers that, the max-comment read is the fallback. */
const TAIL_FIRST_READ = 1024;
const TAIL_MAX_READ = EOCD_MIN_SIZE + 0xffff;
/** Local headers can carry a different extra field from the central one —
 *  read this much past the expected end so one request is usually enough. */
const LOCAL_HEADER_SLACK = 256;
/** Parallel range requests across all files — a browser caps HTTP/1.1
 *  connections per host at 6, so more would only queue. */
const MAX_CONCURRENT_PREVIEWS = 6;

const DEFAULT_SLIDE_SIZE = { cx: 12192000, cy: 6858000 };
/** Same caps the library's own wireframes use (slideTemplatesApi.ts). */
const MAX_PREVIEW_BLOCKS = 24;
const MIN_BLOCK_SIZE_PCT = 1;

/** One library's directory SAS, as the pane's "catalog-ready" reply carries it. */
export interface PreviewLibrarySas {
  readonly sasUrl: string;
  readonly manifestBlobPath: string;
}

/* ── Range reads + zip directory ─────────────────── */

class RangeUnsupportedError extends Error {
  constructor() {
    super("Blob storage ignored the Range header.");
    this.name = "RangeUnsupportedError";
  }
}

async function readRange(url: string, range: string, signal: AbortSignal): Promise<Uint8Array> {
  const res = await fetch(url, {
    method: "GET",
    headers: {
      ...sasVersionHeaders(url, logSlideTemplatesError, "create-slide-preview-sas-version-parse"),
      Range: `bytes=${range}`,
    },
    signal,
  });
  if (res.status === 200) {
    // The whole file is coming — exactly what this module exists to avoid.
    void res.body?.cancel();
    throw new RangeUnsupportedError();
  }
  if (res.status !== 206) throw new Error(`Range read failed (${res.status})`);
  return new Uint8Array(await res.arrayBuffer());
}

interface ZipEntry {
  readonly method: number;
  readonly compressedSize: number;
  readonly nameLength: number;
  readonly localHeaderOffset: number;
}

function findEocd(tail: Uint8Array): number {
  const view = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);
  for (let i = tail.length - EOCD_MIN_SIZE; i >= 0; i--) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) return i;
  }
  return -1;
}

/** Reads the end record + central directory → part name → entry. */
async function readZipDirectory(url: string, signal: AbortSignal): Promise<Map<string, ZipEntry>> {
  let tail = await readRange(url, `-${TAIL_FIRST_READ}`, signal);
  let eocd = findEocd(tail);
  if (eocd === -1) {
    tail = await readRange(url, `-${TAIL_MAX_READ}`, signal);
    eocd = findEocd(tail);
  }
  if (eocd === -1) throw new Error("Not a zip file (no end-of-central-directory record).");

  const end = new DataView(tail.buffer, tail.byteOffset + eocd, EOCD_MIN_SIZE);
  const cdSize = end.getUint32(12, true);
  const cdOffset = end.getUint32(16, true);
  if (cdSize === 0xffffffff || cdOffset === 0xffffffff) throw new Error("ZIP64 packages aren't supported.");

  const cd = await readRange(url, `${cdOffset}-${cdOffset + cdSize - 1}`, signal);
  const view = new DataView(cd.buffer, cd.byteOffset, cd.byteLength);
  const entries = new Map<string, ZipEntry>();
  let at = 0;
  while (at + 46 <= cd.length && view.getUint32(at, true) === CD_SIGNATURE) {
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const name = strFromU8(cd.subarray(at + 46, at + 46 + nameLength));
    entries.set(name, {
      method: view.getUint16(at + 10, true),
      compressedSize: view.getUint32(at + 20, true),
      nameLength,
      localHeaderOffset: view.getUint32(at + 42, true),
    });
    at += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

/** One file's directory, read once, plus a per-part cache — a layout and
 *  master are shared by most slides of a template file. */
interface RemotePackage {
  readonly url: string;
  readonly directory: Promise<Map<string, ZipEntry>>;
  readonly parts: Map<string, Promise<Document | null>>;
}

async function readPartBytes(pkg: RemotePackage, path: string, signal: AbortSignal): Promise<Uint8Array | null> {
  const entry = (await pkg.directory).get(path);
  if (!entry) return null;
  const start = entry.localHeaderOffset;
  const guessEnd = start + 30 + entry.nameLength + entry.compressedSize + LOCAL_HEADER_SLACK;
  let bytes = await readRange(pkg.url, `${start}-${guessEnd - 1}`, signal);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0, true) !== LOCAL_SIGNATURE) throw new Error(`Bad local header for ${path}.`);
  const dataStart = 30 + view.getUint16(26, true) + view.getUint16(28, true);
  const dataEnd = dataStart + entry.compressedSize;
  if (dataEnd > bytes.length) {
    bytes = await readRange(pkg.url, `${start}-${start + dataEnd - 1}`, signal);
  }
  const data = bytes.subarray(dataStart, dataEnd);
  if (entry.method === 0) return data;
  if (entry.method === 8) return inflateSync(data);
  throw new Error(`Unsupported zip compression method ${entry.method} for ${path}.`);
}

function readPart(pkg: RemotePackage, path: string, signal: AbortSignal): Promise<Document | null> {
  let cached = pkg.parts.get(path);
  if (!cached) {
    cached = readPartBytes(pkg, path, signal).then((bytes) =>
      bytes ? new DOMParser().parseFromString(strFromU8(bytes), "application/xml") : null,
    );
    pkg.parts.set(path, cached);
  }
  return cached;
}

/* ── OPC navigation ──────────────────────────────── */

/** "ppt/slides/slide3.xml" → "ppt/slides/_rels/slide3.xml.rels". */
function relsPathFor(partPath: string): string {
  const slash = partPath.lastIndexOf("/");
  return `${partPath.slice(0, slash)}/_rels/${partPath.slice(slash + 1)}.rels`;
}

/** Resolves a relationship Target against the part that owns it. */
function resolveTarget(ownerPath: string, target: string): string {
  if (target.startsWith("/")) return target.slice(1);
  const parts = ownerPath.split("/").slice(0, -1);
  for (const segment of target.split("/")) {
    if (segment === "..") parts.pop();
    else if (segment && segment !== ".") parts.push(segment);
  }
  return parts.join("/");
}

/** The first relationship of `part` whose Type ends with `typeSuffix`, or whose Id is `id`. */
async function relatedPart(
  pkg: RemotePackage,
  partPath: string,
  match: { readonly id?: string; readonly typeSuffix?: string },
  signal: AbortSignal,
): Promise<string | null> {
  const rels = await readPart(pkg, relsPathFor(partPath), signal);
  if (!rels) return null;
  for (const rel of Array.from(rels.getElementsByTagNameNS(RELS_NS, "Relationship"))) {
    const matches = match.id
      ? rel.getAttribute("Id") === match.id
      : (rel.getAttribute("Type") ?? "").endsWith(match.typeSuffix ?? "");
    const target = rel.getAttribute("Target");
    if (matches && target) return resolveTarget(partPath, target);
  }
  return null;
}

/* ── Geometry ────────────────────────────────────── */

interface Rect {
  readonly x: number;
  readonly y: number;
  readonly cx: number;
  readonly cy: number;
}

interface PlaceholderKey {
  readonly type: string;
  readonly idx: string | null;
}

/** Child coords → parent coords, for shapes inside a group. */
type Transform = (rect: Rect) => Rect;
const IDENTITY: Transform = (rect) => rect;

function directChild(parent: Element, ns: string, localName: string): Element | null {
  for (const child of Array.from(parent.children)) {
    if (child.namespaceURI === ns && child.localName === localName) return child;
  }
  return null;
}

function intAttr(el: Element | null, name: string): number | null {
  const raw = el?.getAttribute(name);
  if (raw === null || raw === undefined) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/** `<a:xfrm>`/`<p:xfrm>` → its off/ext rectangle, when complete. */
function xfrmRect(xfrm: Element | null): Rect | null {
  if (!xfrm) return null;
  const off = directChild(xfrm, A_NS, "off");
  const ext = directChild(xfrm, A_NS, "ext");
  const x = intAttr(off, "x");
  const y = intAttr(off, "y");
  const cx = intAttr(ext, "cx");
  const cy = intAttr(ext, "cy");
  return x === null || y === null || cx === null || cy === null ? null : { x, y, cx, cy };
}

/** The shape's own `<p:spPr>`/`<p:grpSpPr>` xfrm, or a graphicFrame's `<p:xfrm>`. */
function shapeRect(shape: Element): Rect | null {
  const props = directChild(shape, P_NS, "spPr") ?? directChild(shape, P_NS, "grpSpPr");
  if (props) return xfrmRect(directChild(props, A_NS, "xfrm"));
  return xfrmRect(directChild(shape, P_NS, "xfrm"));
}

/** The `<p:ph>` under a shape's non-visual properties, if it's a placeholder. */
function placeholderOf(shape: Element): PlaceholderKey | null {
  const nv = Array.from(shape.children).find((child) => child.localName.startsWith("nv"));
  const nvPr = nv ? directChild(nv, P_NS, "nvPr") : null;
  const ph = nvPr ? directChild(nvPr, P_NS, "ph") : null;
  if (!ph) return null;
  return { type: ph.getAttribute("type") ?? "body", idx: ph.getAttribute("idx") };
}

/** Title placeholders match each other across layout levels; so do body-like ones. */
function placeholderFamily(type: string): string {
  if (type === "title" || type === "ctrTitle") return "title";
  if (type === "body" || type === "subTitle" || type === "obj") return "body";
  return type;
}

function roleForPlaceholder(type: string): SlidePreviewRole | null {
  switch (type) {
    case "title":
    case "ctrTitle":
      return "title";
    case "pic":
    case "clipArt":
    case "media":
      return "image";
    case "chart":
      return "chart";
    case "tbl":
      return "table";
    case "dt":
    case "ftr":
    case "sldNum":
    case "hdr":
      return null; // footer furniture — noise at thumbnail size
    default:
      return "body";
  }
}

function hasText(shape: Element): boolean {
  const txBody = directChild(shape, P_NS, "txBody");
  if (!txBody) return false;
  return Array.from(txBody.getElementsByTagNameNS(A_NS, "t")).some((t) => (t.textContent ?? "").trim());
}

function graphicFrameRole(frame: Element): SlidePreviewRole {
  const uri = frame.getElementsByTagNameNS(A_NS, "graphicData")[0]?.getAttribute("uri") ?? "";
  if (uri.includes("chart")) return "chart";
  if (uri.includes("table")) return "table";
  return "other";
}

/** Placeholder positions a layout or master declares, for inheritance. */
function placeholderRects(doc: Document | null): Array<{ readonly key: PlaceholderKey; readonly rect: Rect }> {
  if (!doc) return [];
  const found: Array<{ key: PlaceholderKey; rect: Rect }> = [];
  for (const sp of Array.from(doc.getElementsByTagNameNS(P_NS, "sp"))) {
    const key = placeholderOf(sp);
    const rect = key ? shapeRect(sp) : null;
    if (key && rect) found.push({ key, rect });
  }
  return found;
}

/** Inherited position: same idx first, then same type family — layout, then master. */
function inheritedRect(
  key: PlaceholderKey,
  levels: ReadonlyArray<ReadonlyArray<{ readonly key: PlaceholderKey; readonly rect: Rect }>>,
): Rect | null {
  for (const level of levels) {
    const byIdx = key.idx !== null ? level.find((p) => p.key.idx === key.idx) : undefined;
    if (byIdx) return byIdx.rect;
    const byType = level.find((p) => placeholderFamily(p.key.type) === placeholderFamily(key.type));
    if (byType) return byType.rect;
  }
  return null;
}

interface RawBlock {
  readonly role: SlidePreviewRole;
  readonly rect: Rect;
}

type InheritanceLevels = ReadonlyArray<ReadonlyArray<{ readonly key: PlaceholderKey; readonly rect: Rect }>>;

/** A group's child-coordinate space → its parent's, composed onto `outer`.
 *  A group without a complete xfrm leaves its children's coords as they are. */
function groupTransform(group: Element, outer: Transform): Transform {
  const groupProps = directChild(group, P_NS, "grpSpPr");
  const xfrm = groupProps ? directChild(groupProps, A_NS, "xfrm") : null;
  const rect = xfrmRect(xfrm);
  const chOff = xfrm ? directChild(xfrm, A_NS, "chOff") : null;
  const chExt = xfrm ? directChild(xfrm, A_NS, "chExt") : null;
  const cw = intAttr(chExt, "cx");
  const ch = intAttr(chExt, "cy");
  if (!rect || !cw || !ch) return outer;
  const cx0 = intAttr(chOff, "x") ?? 0;
  const cy0 = intAttr(chOff, "y") ?? 0;
  return (child) =>
    outer({
      x: rect.x + ((child.x - cx0) * rect.cx) / cw,
      y: rect.y + ((child.y - cy0) * rect.cy) / ch,
      cx: (child.cx * rect.cx) / cw,
      cy: (child.cy * rect.cy) / ch,
    });
}

/** A `<p:sp>`'s block: placeholders by type (position inherited when the
 *  slide omits it), plain text boxes as body, empty decoration dropped. */
function textShapeBlock(shape: Element, levels: InheritanceLevels): RawBlock | null {
  const ph = placeholderOf(shape);
  if (!ph) {
    const rect = hasText(shape) ? shapeRect(shape) : null;
    return rect ? { role: "body", rect } : null;
  }
  const role = roleForPlaceholder(ph.type);
  const rect = shapeRect(shape) ?? inheritedRect(ph, levels);
  return role && rect ? { role, rect } : null;
}

/** One non-group shape → its block, in its own coordinate space. */
function shapeBlock(shape: Element, levels: InheritanceLevels): RawBlock | null {
  if (shape.localName === "sp") return textShapeBlock(shape, levels);
  const rect = shapeRect(shape);
  if (!rect) return null;
  if (shape.localName === "pic") return { role: "image", rect };
  if (shape.localName === "graphicFrame") return { role: graphicFrameRole(shape), rect };
  return null;
}

function collectBlocks(tree: Element, transform: Transform, levels: InheritanceLevels, out: RawBlock[]): void {
  for (const shape of Array.from(tree.children)) {
    if (shape.namespaceURI !== P_NS) continue;
    if (shape.localName === "grpSp") {
      collectBlocks(shape, groupTransform(shape, transform), levels, out);
      continue;
    }
    const block = shapeBlock(shape, levels);
    if (block) out.push({ role: block.role, rect: transform(block.rect) });
  }
}

function toPreview(blocks: readonly RawBlock[], size: { cx: number; cy: number }): SlidePreview | null {
  const seen = new Set<string>();
  const result: SlidePreviewBlock[] = [];
  for (const { role, rect } of blocks) {
    const x = clampPct((rect.x / size.cx) * 100);
    const y = clampPct((rect.y / size.cy) * 100);
    const w = roundPct(Math.min(100 - x, (rect.cx / size.cx) * 100));
    const h = roundPct(Math.min(100 - y, (rect.cy / size.cy) * 100));
    if (w < MIN_BLOCK_SIZE_PCT || h < MIN_BLOCK_SIZE_PCT) continue;
    const block = { role, x: roundPct(x), y: roundPct(y), w, h };
    const key = `${block.role}|${block.x}|${block.y}|${block.w}|${block.h}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(block);
    if (result.length >= MAX_PREVIEW_BLOCKS) break;
  }
  return result.length > 0 ? { aspect: size.cx / size.cy, blocks: result } : null;
}

/** One slide (1-based, presentation order) of a remote package → its wireframe. */
async function slidePreview(pkg: RemotePackage, slideIndex: number, signal: AbortSignal): Promise<SlidePreview | null> {
  const presentation = await readPart(pkg, "ppt/presentation.xml", signal);
  if (!presentation) return null;
  const sldSz = presentation.getElementsByTagNameNS(P_NS, "sldSz")[0] ?? null;
  const size = {
    cx: intAttr(sldSz, "cx") ?? DEFAULT_SLIDE_SIZE.cx,
    cy: intAttr(sldSz, "cy") ?? DEFAULT_SLIDE_SIZE.cy,
  };
  // Presentation order, never the part name — see pptxTemplateSlideInsert.ts.
  const relId = presentation.getElementsByTagNameNS(P_NS, "sldId")[slideIndex - 1]?.getAttributeNS(R_NS, "id");
  if (!relId) return null;
  const slidePath = await relatedPart(pkg, "ppt/presentation.xml", { id: relId }, signal);
  if (!slidePath) return null;

  const [slide, layoutPath] = await Promise.all([
    readPart(pkg, slidePath, signal),
    relatedPart(pkg, slidePath, { typeSuffix: "/slideLayout" }, signal),
  ]);
  const spTree = slide?.getElementsByTagNameNS(P_NS, "spTree")[0];
  if (!spTree) return null;

  const [layout, masterPath] = layoutPath
    ? await Promise.all([
        readPart(pkg, layoutPath, signal),
        relatedPart(pkg, layoutPath, { typeSuffix: "/slideMaster" }, signal),
      ])
    : [null, null];
  const master = masterPath ? await readPart(pkg, masterPath, signal) : null;

  const blocks: RawBlock[] = [];
  collectBlocks(spTree, IDENTITY, [placeholderRects(layout), placeholderRects(master)], blocks);
  return toPreview(blocks, size);
}

/* ── Public entry point ──────────────────────────── */

/** "transform-layouts/Cover_Slide.pptx#2" → its folder, file and slide. */
function parseTemplateId(id: string): { folder: string; file: string; slideIndex: number } | null {
  const match = /^(.+)\/([^/#]+)#(\d+)\s*$/.exec(id);
  if (!match) return null;
  const slideIndex = Number.parseInt(match[3]!, 10);
  return slideIndex > 0 ? { folder: match[1]!.split("/").pop()!, file: match[2]!, slideIndex } : null;
}

/** ".../transform-layouts/transform-layouts.signatures.json" → "transform-layouts". */
function folderOfManifest(manifestBlobPath: string): string | null {
  const segments = manifestBlobPath.split("/").filter(Boolean);
  return segments.length >= 2 ? segments[segments.length - 2]! : null;
}

/**
 * Builds a wireframe for each of `templateIds`, in the order given (callers
 * put each slide's recommended pick first), calling `onPreview` as each one
 * lands. Resolves once every id has been tried; failures are logged once per
 * file and otherwise ignored.
 */
export async function fetchTemplateSlidePreviews(
  templateIds: readonly string[],
  libraries: readonly PreviewLibrarySas[],
  signal: AbortSignal,
  onPreview: (templateId: string, preview: SlidePreview) => void,
): Promise<void> {
  const sasByFolder = new Map<string, string>();
  for (const library of libraries) {
    const folder = folderOfManifest(library.manifestBlobPath);
    if (folder && !sasByFolder.has(folder)) sasByFolder.set(folder, library.sasUrl);
  }

  const jobs = [...new Set(templateIds)].flatMap((id) => {
    const parsed = parseTemplateId(id);
    const sasUrl = parsed ? sasByFolder.get(parsed.folder) : undefined;
    if (!parsed || !sasUrl) return [];
    const fileKey = `${parsed.folder}/${parsed.file}`;
    return [{ id, fileKey, slideIndex: parsed.slideIndex, sasUrl, file: parsed.file }];
  });

  const session: PreviewSession = { packages: new Map(), failedFiles: new Set(), signal, onPreview };
  let next = 0;
  // Each worker takes the next job when its last one settles — a chain, not
  // a loop, so jobs overlap across workers but never queue behind each other.
  const worker = async (): Promise<void> => {
    const job = jobs[next++];
    if (!job || signal.aborted) return;
    await runPreviewJob(session, job);
    return worker();
  };
  await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_PREVIEWS, jobs.length) }, worker));
}

interface PreviewJob {
  readonly id: string;
  readonly fileKey: string;
  readonly slideIndex: number;
  readonly sasUrl: string;
  readonly file: string;
}

interface PreviewSession {
  readonly packages: Map<string, RemotePackage>;
  /** Files that already failed a range read — their other slides are skipped. */
  readonly failedFiles: Set<string>;
  readonly signal: AbortSignal;
  readonly onPreview: (templateId: string, preview: SlidePreview) => void;
}

/** The job's file, opened once per session and shared by its other slides. */
function packageFor(session: PreviewSession, job: PreviewJob): RemotePackage {
  const existing = session.packages.get(job.fileKey);
  if (existing) return existing;
  const url = directorySasFileUrl(job.sasUrl, job.file, (message) =>
    logSlideTemplatesError("create-slide-preview-sas-rebuild", message),
  );
  const pkg: RemotePackage = { url, directory: readZipDirectory(url, session.signal), parts: new Map() };
  pkg.directory.catch(() => undefined); // awaited later; this only silences an early rejection
  session.packages.set(job.fileKey, pkg);
  return pkg;
}

async function runPreviewJob(session: PreviewSession, job: PreviewJob): Promise<void> {
  if (session.failedFiles.has(job.fileKey)) return;
  try {
    const preview = await slidePreview(packageFor(session, job), job.slideIndex, session.signal);
    if (preview && !session.signal.aborted) session.onPreview(job.id, preview);
  } catch (error) {
    if (session.signal.aborted) return;
    // One file that can't be range-read won't be readable for its other
    // slides either — skip them rather than repeat the same failure.
    session.failedFiles.add(job.fileKey);
    logSlideTemplatesError(
      "create-slide-preview-read",
      `${job.fileKey}: ${error instanceof Error ? error.message : "preview read failed"}`,
    );
  }
}
